import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

const MAINTENANCE_CHECK_INTERVAL_MS = 30 * 1000; // 30 seconds (for testing; increase in production)
const BYPASS_FLAG = 'bypass_maintenance_check';
const MAINTENANCE_PATH = '/maintenance';

/**
 * Global maintenance check: polls the API every MAINTENANCE_CHECK_INTERVAL_MS.
 * If the current time falls within an active scheduled maintenance window,
 * redirects to the maintenance page (unless bypass_maintenance_check is set
 * in localStorage). Does not run when already on the maintenance page.
 */
export function useMaintenanceCheck(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const isOnMaintenancePage = location.pathname === MAINTENANCE_PATH;
    const bypass = localStorage.getItem(BYPASS_FLAG);

    if (bypass || isOnMaintenancePage) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const check = async () => {
      try {
        const active = await apiService.getMaintenanceStatus();
        if (active) {
          navigate(MAINTENANCE_PATH, { replace: true });
        }
      } catch {
        // Ignore errors (e.g. network) so normal app usage is not disrupted
      }
    };

    check();
    intervalRef.current = setInterval(check, MAINTENANCE_CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [location.pathname, navigate]);
}
